<script setup lang="ts">



const { getProjects } = useHome()
const { data: projects } = await useAsyncData('home-projects', () => getProjects())

const carouselRef = ref<HTMLElement | null>(null)
const activeFilter = ref('Tous')
const isFilterMenuOpen = ref(false)

interface ProjectFilter {
  label: string
  value: string
}

const filters = computed<ProjectFilter[]>(() => {
  const allFilters: ProjectFilter[] = [
    { label: 'Tous', value: 'Tous' },
    { label: 'Jeu', value: 'Jeu' },
    { label: 'Art', value: 'Game Art' },
    { label: 'Son', value: 'Sound Design' },
    { label: 'Game Design', value: 'Game Design' },
    { label: 'Développement', value: 'Game Programming' }
  ]

  if (!projects.value) return [allFilters[0]!]

  return allFilters.filter(f => {
    if (f.value === 'Tous') return true
    if (f.value === 'Jeu') return (projects.value?.length || 0) > 0 
    return projects.value?.some(p => p.formation === f.value) || false
  })
})

const filteredProjects = computed(() => {
  if (!projects.value) return []
  
  if (activeFilter.value === 'Tous') {
    return projects.value
  }
  
  if (activeFilter.value === 'Jeu') {
    // For now, 'Jeu' shows all projects as the data structure doesn't distinguish yet
    // In a real scenario, this might filter by a specific 'Project' type vs 'Portfolio'
    return projects.value
  }

  return projects.value.filter(p => p.formation === activeFilter.value)
})

const setFilter = (filter: string) => {
  activeFilter.value = filter
}
</script>

<template>
  <section class="py-section-py bg-background relative transition-colors duration-300">
    <!-- Background decoration -->
    <div class="absolute inset-0 opacity-5"></div>
    <div class="absolute top-0 left-0 w-full h-32 bg-linear-to-b from-background to-transparent z-10"></div>
    <div class="absolute bottom-0 left-0 w-full h-32 bg-linear-to-t from-background to-transparent z-10"></div>

    <div class="container mx-auto px-container-px relative z-20">
      <div class="flex flex-col xl:flex-row justify-between xl:items-end mb-section-header-mb gap-8">
        <UiSectionHeader 
          align="left"
          titlePrefix="PROJETS"
          titleHighlight="ÉTUDIANTS"
          subtitle="Découvrez les créations réalisées par nos étudiants tout au long de leur cursus."
          class="mb-0 overflow-visible"
        />
        <div class="flex flex-col md:flex-row items-start md:items-center gap-6 w-full xl:w-auto">
          <div class="hidden xl:block">
            <AppButton to="/projets" variant="outline" class="shrink-0 whitespace-nowrap">
              Voir tous les projets
            </AppButton>
          </div>
          
          <!-- Filters -->
          <div class="relative z-30 w-full md:w-auto">
            <!-- Mobile Filter Dropdown Trigger -->
            <UiMobileSelect
              :model-value="activeFilter"
              @update:model-value="setFilter"
              :options="filters.map(f => f.value)"
              label="Filtrer les projets"
              class="md:hidden"
            />

            <!-- Desktop Filters -->
            <div class="hidden md:block w-full overflow-x-auto scrollbar-hide">
              <UiTabs 
                class="whitespace-nowrap" 
                :items="filters.map(f => f.label)" 
                :model-value="filters.find(f => f.value === activeFilter)?.label || 'Tous'"
                @update:model-value="(label: string) => setFilter(filters.find(f => f.label === label)?.value || 'Tous')"
              />
            </div>
          </div>
        </div>
        </div>
        
      <div v-if="filteredProjects.length > 0" class="mt-8">
        <UiCarousel 
          ref="carouselComponentRef" 
          container-tag="ul"
          auto-scroll
          infinite
          :auto-scroll-speed="1"
        >
          <li 
            v-for="(project, index) in [...filteredProjects, ...filteredProjects]" 
            :key="index"
            class="w-[180px] md:w-[240px] flex-none snap-center list-none"
          >
            <ProjectCard :project="project" />
          </li>
        </UiCarousel>
      </div>

      
      <!-- Empty State -->
      <div v-else class="text-center py-12">
        <p class="text-foreground-dim text-lg">Aucun projet trouvé pour cette catégorie.</p>
      </div>

      <!-- Mobile: View All Button -->
      <div class="mt-12 text-center xl:hidden">
        <AppButton to="/projets" variant="outline" class="w-full">
          Voir tous les projets
        </AppButton>
      </div>
    </div>
  </section>
</template>

<style scoped>
.scrollbar-hide::-webkit-scrollbar {
    display: none;
}
.scrollbar-hide {
    -ms-overflow-style: none;
    scrollbar-width: none;
}

.mask-linear-fade {
  mask-image: linear-gradient(to right, transparent 0%, black 5%, black 95%, transparent 100%);
  -webkit-mask-image: linear-gradient(to right, transparent 0%, black 5%, black 95%, transparent 100%);
}
</style>
